/**
 * @fileoverview
 * Global audit route configuration. Maps audit module and backend currentStep
 * to Angular route paths for list and process navigation. Use throughout the
 * app (dashboard, list adapters, etc.) for consistent navigation.
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import {
  ASSET_AUDIT_ROUTE,
  BRANCH_AUDIT_ROUTE,
  CASH_ROUTE,
  GOLD_VERIFICATION_ROUTE,
  KEY_AUDIT_ROUTE,
  LIST_TAB,
  PACKET_INVENTORY_ROUTE,
  REGISTERS_STATUTORY_ROUTE,
  SECURITY_ROUTE,
  SURVEILLANCE_ROUTE,
  TARE_WEIGHT_ROUTE,
  VALUATION_TOOL_ROUTE,
} from './audit-route-segments.const';
import { AUDIT_MODULE } from './audit.const';

/** Backend step name -> frontend route segment for a module */
export type AuditStepMap = Record<string, string>;

export interface AuditModuleRouteConfig {
  /** Route segments to the list page (e.g. ['/cash', 'this-week']) */
  listPath: string[];
  /** Route segments to the process container, without step or uuid (e.g. ['/cash', 'cash-audit-process']) */
  processPath: string[];
  /** Backend currentStep value -> route segment. Used for resume and view. */
  steps: AuditStepMap;
  /** Backend step name for the first step (used after start). */
  firstStep: string;
  /** If true, process URL is .../step/:uuid; if false, .../step only */
  stepPathIncludesUuid?: boolean;
  /**
   * If set, this module uses a single entry route (e.g. branch-details/:uuid) instead of step-based.
   * processPath should point to that entry; firstStep is ignored for start/resume navigation.
   */
  entryPathSegment?: string;
}

/** Module codes - use string keys to avoid circular/parsing issues with AUDIT_MODULE in computed keys */
const MODULE_CASH = AUDIT_MODULE.CASH;
const MODULE_SECURITY = AUDIT_MODULE.SECURITY;
const MODULE_GOLD_VERIFICATION = AUDIT_MODULE.GOLD_VERIFICATION;
const MODULE_VALUATION_TOOL = AUDIT_MODULE.VALUATION_TOOL;
const MODULE_PACKET_INVENTORY = AUDIT_MODULE.PACKET_INVENTORY;
const MODULE_TARE_WEIGHT = AUDIT_MODULE.TARE_WEIGHT;
const MODULE_REGISTER_STATUTORY = AUDIT_MODULE.REGISTER_STATUTORY;
const MODULE_ASSET = AUDIT_MODULE.ASSET;
const MODULE_KEY = AUDIT_MODULE.KEY;
const MODULE_ROUTINE_CHECKING = AUDIT_MODULE.ROUTINE_CHECKING;
const MODULE_LOAN_PROCESS_CHECKING = AUDIT_MODULE.LOAN_PROCESS_CHECKING;
const MODULE_KYC_DISBURSEMENT = AUDIT_MODULE.KYC_DISBURSEMENT;
const MODULE_RELEASE = AUDIT_MODULE.RELEASE;
const MODULE_BRANCH_AUDIT = AUDIT_MODULE.BRANCH_AUDIT;

/**
 * Global config: module code (AUDIT_MODULE) -> list path, process path, step map, first step.
 * Step keys are backend currentStep values (e.g. CAPTURE_DRAWER, ACKNOWLEDGE).
 */
export const AUDIT_MODULE_ROUTE_CONFIG: Record<string, AuditModuleRouteConfig> = {
  [MODULE_CASH]: {
    listPath: ['/cash', LIST_TAB.THIS_WEEK],
    processPath: ['/cash', CASH_ROUTE.PROCESS],
    steps: {
      CAPTURE_DRAWER: CASH_ROUTE.CAPTURE_DRAWER,
      DENOMINATION: CASH_ROUTE.DENOMINATION,
      REGISTER_IMAGE: CASH_ROUTE.REGISTER_IMAGE,
      VARIANCE: CASH_ROUTE.VARIANCE,
      ACKNOWLEDGE: CASH_ROUTE.ACKNOWLEDGE,
      DONE: CASH_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'CAPTURE_DRAWER',
  },

  [MODULE_SECURITY]: {
    listPath: ['/security', LIST_TAB.THIS_WEEK],
    processPath: ['/security', SECURITY_ROUTE.PROCESS],
    steps: {
      SECURITY_STATUS: SECURITY_ROUTE.SECURITY_STATUS,
      VARIANCE: SECURITY_ROUTE.VARIANCE,
      ACKNOWLEDGE: SECURITY_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'SECURITY_STATUS',
  },

  [MODULE_GOLD_VERIFICATION]: {
    listPath: ['/gold', GOLD_VERIFICATION_ROUTE.SEGMENT, LIST_TAB.THIS_WEEK],
    processPath: ['/gold', GOLD_VERIFICATION_ROUTE.SEGMENT],
    steps: {},
    firstStep: '',
    entryPathSegment: GOLD_VERIFICATION_ROUTE.BRANCH_DETAILS,
  },

  [AUDIT_MODULE.VALUATION_TOOL]: {
    listPath: ['/gold', VALUATION_TOOL_ROUTE.SEGMENT, LIST_TAB.THIS_WEEK],
    processPath: ['/gold', VALUATION_TOOL_ROUTE.PROCESS],
    steps: {
      VALUATION_TOOL_STATUS: VALUATION_TOOL_ROUTE.STATUS,
      'valuation-tool-status': VALUATION_TOOL_ROUTE.STATUS,
      VARIANCE: VALUATION_TOOL_ROUTE.VARIANCE,
      variance: VALUATION_TOOL_ROUTE.VARIANCE,
      ACKNOWLEDGE: VALUATION_TOOL_ROUTE.ACKNOWLEDGE,
      acknowledge: VALUATION_TOOL_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'VALUATION_TOOL_STATUS',
    stepPathIncludesUuid: true,
  },

  [MODULE_PACKET_INVENTORY]: {
    listPath: ['/packet-inventory', LIST_TAB.THIS_WEEK],
    processPath: ['/packet-inventory', PACKET_INVENTORY_ROUTE.PROCESS],
    steps: {
      PACKET_VERIFICATION: PACKET_INVENTORY_ROUTE.PACKET_VERIFICATION,
      VARIANCE_CHECK: PACKET_INVENTORY_ROUTE.VARIANCE_CHECK,
      VARIANCE_SUMMARY: PACKET_INVENTORY_ROUTE.VARIANCE_CHECK,
      STOCK_IRREGULARITIES: PACKET_INVENTORY_ROUTE.VARIANCE_CHECK,
      REGISTER_MISMATCH: PACKET_INVENTORY_ROUTE.VARIANCE_CHECK,
      MISMATCH_HANDLING: PACKET_INVENTORY_ROUTE.VARIANCE_CHECK,
      ADDITIONAL_CHECKS: PACKET_INVENTORY_ROUTE.ACKNOWLEDGE,
      ACKNOWLEDGE: PACKET_INVENTORY_ROUTE.ACKNOWLEDGE,
      DONE: PACKET_INVENTORY_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'PACKET_VERIFICATION',
    stepPathIncludesUuid: true,
  },

  [MODULE_TARE_WEIGHT]: {
    listPath: ['/tare-weight', LIST_TAB.THIS_WEEK],
    processPath: ['/tare-weight', TARE_WEIGHT_ROUTE.PROCESS],
    steps: {
      TARE_WEIGHT_VERIFICATION: TARE_WEIGHT_ROUTE.VERIFICATION,
      VARIANCE_CHECK: TARE_WEIGHT_ROUTE.VARIANCE,
      VARIANCE_SUMMARY: TARE_WEIGHT_ROUTE.VARIANCE,
      MISMATCH_HANDLING: TARE_WEIGHT_ROUTE.VARIANCE,
      STOCK_IRREGULARITIES: TARE_WEIGHT_ROUTE.VARIANCE,
      ACKNOWLEDGE: TARE_WEIGHT_ROUTE.ACKNOWLEDGE,
      DONE: TARE_WEIGHT_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'TARE_WEIGHT_VERIFICATION',
    stepPathIncludesUuid: true,
  },

  [MODULE_REGISTER_STATUTORY]: {
    listPath: ['/registers-and-statutory', LIST_TAB.THIS_WEEK],
    processPath: ['/registers-and-statutory', REGISTERS_STATUTORY_ROUTE.PROCESS],
    steps: {
      REGISTERS: REGISTERS_STATUTORY_ROUTE.REGISTERS,
      STATUTORY: REGISTERS_STATUTORY_ROUTE.STATUTORY,
      SUMMARY: REGISTERS_STATUTORY_ROUTE.VARIANCE,
      ACKNOWLEDGE: REGISTERS_STATUTORY_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'REGISTERS',
  },

  [MODULE_ASSET]: {
    listPath: ['/asset-management', ASSET_AUDIT_ROUTE.SEGMENT, LIST_TAB.THIS_WEEK],
    processPath: ['/asset-management', ASSET_AUDIT_ROUTE.SEGMENT, ASSET_AUDIT_ROUTE.PROCESS],
    steps: {
      ASSET_ENTRY: ASSET_AUDIT_ROUTE.NEW_ASSET_AUDIT,
      ASSET_AUDIT: ASSET_AUDIT_ROUTE.NEW_ASSET_AUDIT,
      VARIANCE: ASSET_AUDIT_ROUTE.VARIANCE,
      ACKNOWLEDGE: ASSET_AUDIT_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'ASSET_ENTRY',
  },

  [MODULE_KEY]: {
    listPath: ['/asset-management', KEY_AUDIT_ROUTE.SEGMENT, LIST_TAB.THIS_WEEK],
    processPath: ['/asset-management', KEY_AUDIT_ROUTE.SEGMENT, KEY_AUDIT_ROUTE.PROCESS],
    steps: {
      KEY_VERIFICATION: KEY_AUDIT_ROUTE.VERIFICATION,
      VARIANCE_CHECK: KEY_AUDIT_ROUTE.VARIANCE_CHECK,
      VARIANCE: KEY_AUDIT_ROUTE.VARIANCE_CHECK,
      ACKNOWLEDGE: KEY_AUDIT_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'KEY_VERIFICATION',
    stepPathIncludesUuid: true,
  },

  [MODULE_ROUTINE_CHECKING]: {
    listPath: ['/surveillance', SURVEILLANCE_ROUTE.ROUTINE_CHECKING, LIST_TAB.THIS_WEEK],
    processPath: ['/surveillance', SURVEILLANCE_ROUTE.ROUTINE_CHECKING, SURVEILLANCE_ROUTE.AUDIT_PROCESS],
    steps: {},
    firstStep: '',
    stepPathIncludesUuid: true,
  },

  [MODULE_LOAN_PROCESS_CHECKING]: {
    listPath: ['/surveillance', SURVEILLANCE_ROUTE.LOAN_PROCESS_CHECKING, LIST_TAB.THIS_WEEK],
    processPath: ['/surveillance', SURVEILLANCE_ROUTE.LOAN_PROCESS_CHECKING, SURVEILLANCE_ROUTE.AUDIT_PROCESS],
    steps: {},
    firstStep: '',
    stepPathIncludesUuid: true,
  },

  [MODULE_KYC_DISBURSEMENT]: {
    listPath: ['/online', 'kyc-and-disbursement', LIST_TAB.ALL],
    processPath: ['/online', 'kyc-and-disbursement'],
    steps: {},
    firstStep: '',
  },

  [MODULE_RELEASE]: {
    listPath: ['/online', 'release', LIST_TAB.ALL],
    processPath: ['/online', 'release'],
    steps: {},
    firstStep: '',
  },

  [MODULE_BRANCH_AUDIT]: {
    listPath: ['/branch-audit'],
    processPath: ['/branch-audit', BRANCH_AUDIT_ROUTE.PROCESS],
    steps: {
      AUDIT: BRANCH_AUDIT_ROUTE.FILL_FORM,
      FILL_FORM: BRANCH_AUDIT_ROUTE.FILL_FORM,
      ACKNOWLEDGE: BRANCH_AUDIT_ROUTE.ACKNOWLEDGE,
    },
    firstStep: 'AUDIT',
  },
};

/** Map dashboard recent-audit type (display name) to module code for route lookup */
export const AUDIT_TYPE_DISPLAY_TO_MODULE: Record<string, string> = {
  'Cash Audit': AUDIT_MODULE.CASH,
  'Gold Audit': AUDIT_MODULE.GOLD_VERIFICATION,
  'Valuation Tool': AUDIT_MODULE.VALUATION_TOOL,
  'Packet Inventory': AUDIT_MODULE.PACKET_INVENTORY,
  'Surveillance (Routine Checking)': AUDIT_MODULE.ROUTINE_CHECKING,
  'Surveillance (Loan Process Checking)': AUDIT_MODULE.LOAN_PROCESS_CHECKING,
  'Tare Weight': AUDIT_MODULE.TARE_WEIGHT,
  'Registers & Statutory': AUDIT_MODULE.REGISTER_STATUTORY,
  'KYC Disbursement': AUDIT_MODULE.KYC_DISBURSEMENT,
  'Release': AUDIT_MODULE.RELEASE,
  'Security': AUDIT_MODULE.SECURITY,
  'Security Audit': AUDIT_MODULE.SECURITY,
  'Asset Audit': AUDIT_MODULE.ASSET,
  'Asset': AUDIT_MODULE.ASSET,
  'Key Audit': AUDIT_MODULE.KEY,
  'Branch Audit': AUDIT_MODULE.BRANCH_AUDIT,
};

/**
 * Resolve module code from audit type (display name from backend) or from module field.
 */
export function getModuleCodeFromAudit(audit: { type?: string; module?: string }): string | null {
  const type = (audit?.type ?? '').trim();
  const module = (audit?.module ?? '').trim();
  if (module && AUDIT_MODULE_ROUTE_CONFIG[module]) return module;
  return AUDIT_TYPE_DISPLAY_TO_MODULE[type] ?? null;
}

/**
 * Return route path array for the audit list page for the given module.
 */
export function getAuditListRoute(moduleCode: string): string[] | null {
  const config = AUDIT_MODULE_ROUTE_CONFIG[moduleCode];
  return config ? [...config.listPath] : null;
}

/**
 * Return route path array for the audit process. Use after start (step = firstStep) or
 * after resume (step = currentStep from API). Pass uuid when stepPathIncludesUuid is true.
 */
export function getAuditProcessRoute(
  moduleCode: string,
  options: { step?: string; uuid?: string }
): string[] | null {
  const config = AUDIT_MODULE_ROUTE_CONFIG[moduleCode];
  if (!config) return null;

  const { step, uuid } = options;

  if (config.entryPathSegment) {
    if (uuid) return [...config.processPath, config.entryPathSegment, uuid];
    return [...config.processPath, config.entryPathSegment];
  }

  const stepSegment = step
    ? (config.steps[step] ?? config.steps[step.toUpperCase()] ?? config.steps[config.firstStep])
    : config.steps[config.firstStep];
  if (!stepSegment) {
    if (config.stepPathIncludesUuid && uuid) return [...config.processPath, uuid];
    return [...config.processPath];
  }

  const path = [...config.processPath, stepSegment];
  if (config.stepPathIncludesUuid && uuid) path.push(uuid);
  return path;
}
